import { Button } from '@components'
import { PreviewHookProps } from '@query/hooks/useRelationSuggestions'
import React from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { FaCaretDown, FaPlus, FaWandMagicSparkles } from 'react-icons/fa6'
import { FaLink } from 'react-icons/fa'

import {
  EmptyState,
  FormEmptyBox,
  FormEmptyLeft,
  FormFallbackTrigger,
  FormSuggestionStack,
  RelationLead,
  RelationLeadRow,
  RelationListWrapperSuggestion,
  RelationsHeader,
  RelationsContainer,
  RelationsMapperContent,
  RelationMapperThemeMode,
  SuggestionLeadIcon
} from './RelationMapper.styled'
import { DocumentRelationContextInput } from './relationContext'
import { getSuggestionPromptKey } from './relationMapperUtils'
import { RelationGroup } from './RelationGroup'
import { RelationSelectionModal } from './RelationSelectionModal'
import { DocumentRelationFormValue, RelationListItem } from './RelationRow'
import { useRelationLeadContent } from './useRelationLeadContent'
import { useRelationMapperController } from './useRelationMapperController'

export type { DocumentRelationFormValue }

export type RelationMapperHeaderRenderProps = {
  title: React.ReactNode
  lead: React.ReactNode | null
  addMenu: React.ReactNode | null
  readOnly: boolean
  themeMode: RelationMapperThemeMode
  hasRelations: boolean
}

interface RelationMapperProps extends DocumentRelationContextInput {
  value?: DocumentRelationFormValue[]
  onChange?: (relations: DocumentRelationFormValue[]) => void
  documentId?: number
  readOnly?: boolean
  /**
   * `form` — empty/suggestion entry UI for DocumentForm / SuggestionForm.
   * `detail` — existing read/write widget used in DocumentOverview (unchanged).
   */
  layoutVariant?: 'form' | 'detail'
  renderHeader?: (options: RelationMapperHeaderRenderProps) => React.ReactNode
  addButtonVariant?: 'default' | 'icon'
  themeMode?: RelationMapperThemeMode
  suggestionsEnabled?: boolean
  canApplySuggestion?: boolean
  previewData?: Omit<PreviewHookProps, 'enabled'>
  disableRelationPreview?: boolean
}

export const RelationMapper: React.FC<RelationMapperProps> = ({
  value,
  onChange,
  documentId,
  readOnly = false,
  layoutVariant = 'detail',
  renderHeader,
  themeMode = 'light',
  suggestionsEnabled = true,
  canApplySuggestion = !readOnly,
  previewData,
  addButtonVariant = 'default',
  sourceDocumentAmount,
  sourceDocumentBalance,
  dueAccountEffect,
  availableLinkKinds,
  accrualPairDueEffect,
  hasAccrualPairDueMismatch,
  accrualPairInvoiceTotal,
  accrualPairPaymentTotal,
  disableRelationPreview = false
}) => {
  const { t } = useTranslation()
  const {
    relations,
    effectiveReadOnly,
    effectiveCanApplySuggestion,
    invoiceAmount,
    networkInvoiceTotal,
    networkPaymentTotal,
    liveOpenAmount,
    hasMismatch,
    preferredBalanceSign,
    suggestion,
    shouldShowSuggestion,
    isApplyingSuggestion,
    suggestionApplyLabel,
    handleApplySuggestion,
    handleRemoveRelation,
    isModalOpen,
    modalLinkKind,
    modalSelection,
    modalInitialSelection,
    availableLinkKindMetas,
    setModalSelection,
    handleConfirmSelection,
    handleCloseModal,
    renderQuickActionMenu
  } = useRelationMapperController({
    value,
    onChange,
    documentId,
    readOnly,
    suggestionsEnabled,
    canApplySuggestion,
    previewData,
    sourceDocumentAmount,
    sourceDocumentBalance,
    dueAccountEffect,
    availableLinkKinds,
    accrualPairDueEffect,
    hasAccrualPairDueMismatch,
    accrualPairInvoiceTotal,
    accrualPairPaymentTotal
  })

  const addMenu = renderQuickActionMenu(
    addButtonVariant === 'icon' ? (
      <Button
        type="button"
        isSecondary
        size="sm"
        icon={<FaPlus />}
        aria-label={t('documentRelations.addButton')}
      />
    ) : (
      <Button type="button" iconRight={<FaCaretDown />} isSecondary size="sm">
        {t('documentRelations.addButton')}
      </Button>
    )
  )

  const isFormLayout = layoutVariant === 'form'
  const showFormEntryStates = isFormLayout && relations.length === 0
  // In form empty/suggestion states the Linkitä action lives inside the box, not the header.
  const headerAddMenu = isFormLayout && relations.length === 0 ? null : addMenu
  const lead = useRelationLeadContent(relations, {
    sourceAmount: invoiceAmount,
    openAmount: liveOpenAmount,
    hasMismatch,
    networkInvoiceTotal,
    networkPaymentTotal
  })
  const title = t('documentRelations.title')

  const formEmptyAddMenu = renderQuickActionMenu(
    <Button type="button" iconRight={<FaCaretDown />} isSecondary size="sm">
      {t('documentRelations.suggestions.link')}
    </Button>
  )

  const formFallbackAddMenu = renderQuickActionMenu(
    <FormFallbackTrigger type="button" isSecondary>
      {t('documentRelations.suggestions.findAnother')}
    </FormFallbackTrigger>
  )

  const renderHeaderNode = (): React.ReactNode => {
    if (renderHeader) {
      return renderHeader({
        title,
        lead,
        addMenu: headerAddMenu,
        readOnly: effectiveReadOnly,
        themeMode,
        hasRelations: relations.length > 0
      })
    }

    if (isFormLayout) {
      return (
        <RelationsHeader $themeMode={themeMode}>
          <h4>{title}</h4>
          {headerAddMenu}
        </RelationsHeader>
      )
    }

    if (headerAddMenu) {
      return (
        <RelationsHeader $themeMode={themeMode} $alignEnd>
          {headerAddMenu}
        </RelationsHeader>
      )
    }

    return null
  }

  const headerNode = renderHeaderNode()

  const hasVisibleHeader = Boolean(headerNode)

  const renderSuggestionNode = (): React.ReactNode => {
    if (!shouldShowSuggestion || !suggestion) {
      return null
    }

    const suggestionCard = (
      <>
        <RelationLeadRow>
          <SuggestionLeadIcon aria-hidden>
            <FaWandMagicSparkles />
          </SuggestionLeadIcon>
          <RelationLead $themeMode={themeMode}>
            <Trans i18nKey={getSuggestionPromptKey(suggestion.role, suggestion.type)} />
          </RelationLead>
        </RelationLeadRow>
        <RelationListWrapperSuggestion $themeMode={themeMode}>
          <RelationListItem
            relation={{
              related_document: suggestion.related_document,
              role: suggestion.role,
              type: suggestion.type
            }}
            onRemove={() => undefined}
            readOnly
            themeMode={themeMode}
            disableRelationPreview={disableRelationPreview}
            onApply={effectiveCanApplySuggestion ? handleApplySuggestion : undefined}
            applyLabel={suggestionApplyLabel}
            isApplying={isApplyingSuggestion}
          />
        </RelationListWrapperSuggestion>
      </>
    )

    if (isFormLayout) {
      return (
        <FormSuggestionStack>
          {suggestionCard}
          {formFallbackAddMenu}
        </FormSuggestionStack>
      )
    }

    return suggestionCard
  }

  const suggestionNode = renderSuggestionNode()

  const renderFormEmptyNode = (): React.ReactNode => {
    if (!showFormEntryStates || shouldShowSuggestion) {
      return null
    }

    return (
      <FormEmptyBox>
        <FormEmptyLeft>
          <FaLink aria-hidden />
          <span>{t('documentRelations.emptyStateShort')}</span>
        </FormEmptyLeft>
        {formEmptyAddMenu}
      </FormEmptyBox>
    )
  }

  const formEmptyNode = renderFormEmptyNode()

  const renderRelationsContentNode = (): React.ReactNode => {
    if (relations.length > 0) {
      return (
        <RelationGroup
          relations={relations}
          onRemove={handleRemoveRelation}
          readOnly={effectiveReadOnly}
          themeMode={themeMode}
          sourceAmount={invoiceAmount}
          openAmount={liveOpenAmount}
          hasMismatch={hasMismatch}
          networkInvoiceTotal={networkInvoiceTotal}
          networkPaymentTotal={networkPaymentTotal}
          disableRelationPreview={disableRelationPreview}
          hideLead={Boolean(renderHeader)}
        />
      )
    }

    if (!shouldShowSuggestion && !showFormEntryStates) {
      return <EmptyState $themeMode={themeMode}>{t('documentRelations.emptyState')}</EmptyState>
    }

    return null
  }

  const relationsContentNode = renderRelationsContentNode()

  return (
    <RelationsContainer $compactHeader={hasVisibleHeader}>
      {headerNode}
      <RelationsMapperContent $offsetBelowHeader={hasVisibleHeader}>
        {suggestionNode}
        {formEmptyNode}
        {relationsContentNode}
      </RelationsMapperContent>

      {!effectiveReadOnly && (
        <RelationSelectionModal
          isOpen={isModalOpen}
          linkKind={modalLinkKind}
          selection={modalSelection}
          initialSelection={modalInitialSelection}
          onSelectionChange={setModalSelection}
          onConfirm={handleConfirmSelection}
          onClose={handleCloseModal}
          documentId={documentId}
          activeRelations={relations}
          targetAmount={invoiceAmount}
          preferredBalanceSign={preferredBalanceSign}
          linkKindMetas={availableLinkKindMetas}
        />
      )}
    </RelationsContainer>
  )
}

export default RelationMapper
